import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Download,
  Copy,
  CheckCircle,
  Code,
  Key,
  FileJson,
  FileSpreadsheet,
  RefreshCw,
  Eye,
  EyeOff,
  Info,
  Sparkles,
  Calendar
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { useOfferingType } from '@/hooks/useOfferingType';

interface QuestionWheel {
  id: string;
  name: string;
  question_count: number;
  answered_count: number;
  created_at: string;
}

interface Category {
  name: string;
  product_count: number;
}

function ServiceFeedNotice() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Feed Export</h1>
        <p className="text-muted-foreground">
          Export enriched product feeds with Q&amp;A data, benefit scores, and persona matching.
        </p>
      </div>
      <Alert>
        <Calendar className="h-4 w-4" />
        <AlertDescription>
          Feed export is available for product sites. Service sites use booking system integrations instead.
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default function FeedExport() {
  const { selectedSite } = useSite();
  const offeringType = useOfferingType();
  const [categories, setCategories] = useState<Category[]>([]);
  const [questionWheels, setQuestionWheels] = useState<QuestionWheel[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedWheel, setSelectedWheel] = useState<string>('');
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);

  // AEO Export Options
  const [includeCategoryAEO, setIncludeCategoryAEO] = useState(true);
  const [includeProductAEO, setIncludeProductAEO] = useState(false);
  const [includeAEOQuestions, setIncludeAEOQuestions] = useState(true);
  const [aeoStatus, setAeoStatus] = useState<'approved' | 'all'>('approved');
  const [includeReviewerInfo, setIncludeReviewerInfo] = useState(true);

  // Change Detection Options (section versioning filters)
  const [changedSince, setChangedSince] = useState('');
  const [changedSection, setChangedSection] = useState('');
  const [includeSectionVersions, setIncludeSectionVersions] = useState(false);
  const [hashesOnly, setHashesOnly] = useState(false);
  const [showChangeDetection, setShowChangeDetection] = useState(false);

  // Load categories and question wheels when site changes
  useEffect(() => {
    if (selectedSite?.id) {
      loadCategories();
      loadQuestionWheels();
    }
  }, [selectedSite?.id]);

  // Load API key
  useEffect(() => {
    loadApiKey();
  }, []);

  const loadApiKey = async () => {
    try {
      const response = await authFetch('/api/users/me/api-key');
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
      }
    } catch (error) {
      console.error('Error loading API key:', error);
    }
  };

  const generateApiKey = async () => {
    setIsGeneratingKey(true);
    try {
      const response = await authFetch('/api/users/me/api-key', {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
      }
    } catch (error) {
      console.error('Error generating API key:', error);
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await authFetch(`/api/sites/${selectedSite?.id}/product-categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadQuestionWheels = async () => {
    try {
      const response = await authFetch(`/api/sites/${selectedSite?.id}/question-wheels`);
      if (response.ok) {
        const data = await response.json();
        // Response is an array directly, not { wheels: [...] }
        const wheels = Array.isArray(data) ? data : (data.wheels || []);
        // Map to expected interface
        setQuestionWheels(wheels.map((w: any) => ({
          id: w.id,
          name: w.center_term || 'Unnamed Wheel',
          question_count: w.total_questions || 0,
          answered_count: w.answered_count || 0,
          created_at: w.created_at
        })));
      }
    } catch (error) {
      console.error('Error loading question wheels:', error);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExportByCategory = async () => {
    if (!selectedCategory || !selectedSite?.id) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        format,
        includeEnrichments: 'true',
        includeProductQuestions: 'true',
        // AEO Content Options
        includeCategoryAEO: includeCategoryAEO.toString(),
        includeProductAEO: includeProductAEO.toString(),
        includeAEOQuestions: includeAEOQuestions.toString(),
        aeoStatus: aeoStatus === 'all' ? 'any' : 'approved',
        includeReviewerInfo: includeReviewerInfo.toString()
      });

      // Change Detection params (section versioning)
      if (changedSince) params.set('changed_since', new Date(changedSince).toISOString());
      if (changedSection && changedSection !== '_all') params.set('changed_section', changedSection);
      if (includeSectionVersions) params.set('include_section_versions', 'true');
      if (hashesOnly) params.set('hashes_only', 'true');

      const response = await authFetch(
        `/api/sites/${selectedSite.id}/categories/${encodeURIComponent(selectedCategory)}/export/enriched-feed?${params}`
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `enriched-feed-${selectedCategory.toLowerCase().replace(/\s+/g, '-')}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Export failed:', await response.text());
      }
    } catch (error) {
      console.error('Error exporting feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportByWheel = async () => {
    if (!selectedWheel || !selectedSite?.id) return;

    setLoading(true);
    try {
      const response = await authFetch(
        `/api/sites/${selectedSite.id}/question-wheels/${selectedWheel}/export-feed`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            format,
            includeStatus: ['approved', 'pending']
          })
        }
      );

      if (response.ok) {
        if (format === 'csv') {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `enriched-feed-wheel-${selectedWheel}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } else {
          const data = await response.json();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `enriched-feed-wheel-${selectedWheel}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      } else {
        console.error('Export failed:', await response.text());
      }
    } catch (error) {
      console.error('Error exporting feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const baseUrl = window.location.origin.replace('3000', '3001');

  if (offeringType === 'service') {
    return <ServiceFeedNotice />;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Feed Export</h1>
        <p className="text-muted-foreground">
          Export enriched product feeds with Q&A data, benefit scores, and persona matching.
        </p>
      </div>

      {offeringType === 'mixed' && (
        <Alert>
          <Calendar className="h-4 w-4" />
          <AlertDescription>
            This site has both products and services. The feed export below covers products.
            Service sites use booking system integrations instead.
          </AlertDescription>
        </Alert>
      )}

      {!selectedSite && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the header to view export options.
          </AlertDescription>
        </Alert>
      )}

      {selectedSite && (
        <Tabs defaultValue="manual" className="space-y-4">
          <TabsList>
            <TabsTrigger value="manual" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Manual Export
            </TabsTrigger>
            <TabsTrigger value="api" className="flex items-center gap-2">
              <Code className="h-4 w-4" />
              API Documentation
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Export by Category */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5" />
                    Export by Category
                  </CardTitle>
                  <CardDescription>
                    Export all products in a category with their enrichments
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.name} value={cat.name}>
                            {cat.name} ({cat.product_count} products)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Format</Label>
                    <Select value={format} onValueChange={(v) => setFormat(v as 'csv' | 'json')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="h-4 w-4" />
                            CSV (Spreadsheet)
                          </div>
                        </SelectItem>
                        <SelectItem value="json">
                          <div className="flex items-center gap-2">
                            <FileJson className="h-4 w-4" />
                            JSON (Structured)
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator className="my-4" />

                  {/* AEO Content Options */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      AEO Content Options
                    </Label>

                    <div className="space-y-2 pl-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeCategoryAEO"
                          checked={includeCategoryAEO}
                          onCheckedChange={(checked) => setIncludeCategoryAEO(checked as boolean)}
                        />
                        <label htmlFor="includeCategoryAEO" className="text-sm cursor-pointer">
                          Category AEO (featured snippets, buyer guides)
                        </label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeAEOQuestions"
                          checked={includeAEOQuestions}
                          onCheckedChange={(checked) => setIncludeAEOQuestions(checked as boolean)}
                        />
                        <label htmlFor="includeAEOQuestions" className="text-sm cursor-pointer">
                          AEO FAQ Questions & Answers
                        </label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeProductAEO"
                          checked={includeProductAEO}
                          onCheckedChange={(checked) => setIncludeProductAEO(checked as boolean)}
                        />
                        <label htmlFor="includeProductAEO" className="text-sm cursor-pointer">
                          Product AEO (why-buy, differentiators)
                        </label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeReviewerInfo"
                          checked={includeReviewerInfo}
                          onCheckedChange={(checked) => setIncludeReviewerInfo(checked as boolean)}
                        />
                        <label htmlFor="includeReviewerInfo" className="text-sm cursor-pointer">
                          Include reviewer info (for citations)
                        </label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Content Status Filter</Label>
                      <Select value={aeoStatus} onValueChange={(v) => setAeoStatus(v as 'approved' | 'all')}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="approved">Approved content only</SelectItem>
                          <SelectItem value="all">All generated content</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  {/* Change Detection (Section Versioning) */}
                  <div className="space-y-3">
                    <button
                      type="button"
                      className="flex items-center gap-2 text-sm font-medium cursor-pointer"
                      onClick={() => setShowChangeDetection(!showChangeDetection)}
                    >
                      <Calendar className="h-4 w-4 text-blue-500" />
                      Change Detection
                      <Badge variant="outline" className="ml-1 text-xs">Versioning filters</Badge>
                      <span className="text-muted-foreground text-xs ml-auto">
                        {showChangeDetection ? '▼' : '▶'}
                      </span>
                    </button>

                    {showChangeDetection && (
                      <div className="space-y-3 pl-2 border-l-2 border-blue-200 ml-2">
                        <div className="space-y-1">
                          <Label className="text-sm">Changed since</Label>
                          <Input
                            type="datetime-local"
                            value={changedSince}
                            onChange={(e) => setChangedSince(e.target.value)}
                            className="h-9"
                          />
                          <p className="text-xs text-muted-foreground">Only export products with changes after this date</p>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-sm">Section filter</Label>
                          <Select value={changedSection} onValueChange={setChangedSection}>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Any change" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_all">Any change</SelectItem>
                              <SelectItem value="summary">Summary (why_buy, full_description, differentiators)</SelectItem>
                              <SelectItem value="best_for">Best For</SelectItem>
                              <SelectItem value="buyer_profiles">Buyer Profiles</SelectItem>
                              <SelectItem value="use_cases">Use Cases</SelectItem>
                              <SelectItem value="benefits">Benefits</SelectItem>
                              <SelectItem value="faq">FAQ</SelectItem>
                              <SelectItem value="comparison">Comparison</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">Narrows <code>changed_since</code> to a single AEO section.</p>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="includeSectionVersions"
                            checked={includeSectionVersions}
                            onCheckedChange={(checked) => setIncludeSectionVersions(checked as boolean)}
                          />
                          <label htmlFor="includeSectionVersions" className="text-sm cursor-pointer">
                            Include per-column version hashes (+ aeo_content_hash rollup)
                          </label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="hashesOnly"
                            checked={hashesOnly}
                            onCheckedChange={(checked) => setHashesOnly(checked as boolean)}
                          />
                          <label htmlFor="hashesOnly" className="text-sm cursor-pointer">
                            Hashes only (probe mode — no content)
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleExportByCategory}
                    disabled={!selectedCategory || loading}
                    className="w-full"
                  >
                    {loading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Enriched Feed
                  </Button>
                </CardContent>
              </Card>

              {/* Export by Question Wheel */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileJson className="h-5 w-5" />
                    Export by Question Wheel
                  </CardTitle>
                  <CardDescription>
                    Export products with their Q&A from a question wheel
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Question Wheel</Label>
                    <Select value={selectedWheel} onValueChange={setSelectedWheel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a question wheel" />
                      </SelectTrigger>
                      <SelectContent>
                        {questionWheels.map((wheel) => (
                          <SelectItem key={wheel.id} value={wheel.id}>
                            {wheel.name} ({wheel.answered_count}/{wheel.question_count} answered)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Format</Label>
                    <Select value={format} onValueChange={(v) => setFormat(v as 'csv' | 'json')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="h-4 w-4" />
                            CSV (Spreadsheet)
                          </div>
                        </SelectItem>
                        <SelectItem value="json">
                          <div className="flex items-center gap-2">
                            <FileJson className="h-4 w-4" />
                            JSON (Structured)
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleExportByWheel}
                    disabled={!selectedWheel || loading}
                    className="w-full"
                  >
                    {loading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Feed
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="api" className="space-y-4">
            {/* API Key Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  API Key
                </CardTitle>
                <CardDescription>
                  Use your API key to access the export endpoints programmatically
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Input
                    type={showApiKey ? "text" : "password"}
                    value={apiKey || "No API key generated"}
                    readOnly
                    className="flex-1 font-mono"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(apiKey || "", "api-key")}
                    disabled={!apiKey}
                  >
                    {copiedId === "api-key" ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {!apiKey && (
                  <Button onClick={generateApiKey} disabled={isGeneratingKey}>
                    {isGeneratingKey ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="h-4 w-4 mr-2" />
                    )}
                    Generate API Key
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* API Endpoints Documentation */}
            <Card>
              <CardHeader>
                <CardTitle>Available Endpoints</CardTitle>
                <CardDescription>
                  Use these endpoints to export enriched product feeds programmatically
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Export by Category Endpoint */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">GET</Badge>
                    <span className="font-semibold">Export by Category</span>
                  </div>
                  <div className="bg-muted p-3 rounded-md font-mono text-sm break-all">
                    /api/sites/{'{siteId}'}/categories/{'{categoryName}'}/export/enriched-feed
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium mb-1">Query Parameters:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>format</code>: 'csv' | 'json' (default: csv)</li>
                      <li><code>useCanonicalNames</code>: 'true' | 'false' - Use standardized attribute names</li>
                      <li><code>includeDualFormat</code>: 'true' | 'false' - Include both original + canonical</li>
                      <li><code>includeEnrichments</code>: 'true' | 'false' - Include Q&A, benefit scores</li>
                      <li><code>includeProductQuestions</code>: 'true' | 'false' - Include product questions</li>
                    </ul>
                    <p className="font-medium mt-3 mb-1">AEO Content Parameters:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>includeCategoryAEO</code>: 'true' | 'false' - Include category AEO (featured snippets, buyer guides)</li>
                      <li><code>includeProductAEO</code>: 'true' | 'false' - Include product-level AEO content</li>
                      <li><code>includeAEOQuestions</code>: 'true' | 'false' - Include AEO FAQ questions & answers</li>
                      <li><code>aeoStatus</code>: 'approved' | 'any' - Filter by content approval status</li>
                      <li><code>includeReviewerInfo</code>: 'true' | 'false' - Include reviewer details for citations</li>
                      <li><code>aeoLanguage</code>: 'es' | 'en' | etc. - Language for AEO content (default: es). Use <code>all</code> to receive every available translation in one response — translatable AEO fields and per-row hashes become per-language maps; FAQs come back as structured arrays of <code>{'{question, answer, category}'}</code>. JSON only.</li>
                    </ul>

                    <p className="font-medium mt-3 mb-1">Filtering Parameters:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>filterByAeoStatus</code>: 'true' | 'false' - Only return products with AEO content matching aeoStatus</li>
                      <li><code>pendingDelivery</code>: 'true' | 'false' - Only products with AEO not yet acknowledged</li>
                      <li><code>pendingRepublish</code>: 'true' | 'false' - Only products needing (re)publication (never published OR updated since last publish)</li>
                      <li><code>faqFormat</code>: 'separate' | 'combined' - FAQ format (Q|Q|Q + A|A|A vs Q:A|Q:A)</li>
                    </ul>
                    <p className="font-medium mt-3 mb-1">Versioning / Change Detection Parameters:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>include_section_versions</code>: 'true' — append per-column SHA256 hashes + <code>updated_at</code> timestamps to each row under <code>section_versions</code>, keyed by the feed column name (e.g. <code>aeo_product_faqs</code>, <code>aeo_comparison_verdict</code>). Also adds a per-row <code>aeo_content_hash</code> rollup and <code>aeo_content_updated_at</code> (latest AEO regeneration across all columns) — store these per <code>item_group_id</code> to detect whether anything in the payload changed.</li>
                      <li><code>hashes_only</code>: 'true' — return the normal row schema with AEO content columns stripped, but with <code>section_versions</code>, <code>aeo_content_hash</code>, and <code>aeo_content_updated_at</code> present. Use for cheap polling: compare the stored <code>aeo_content_hash</code>; if different, inspect the per-column hashes to decide which fields to refetch. Always returns JSON.</li>
                      <li><code>changed_since</code>: ISO 8601 timestamp — only return products regenerated after this date (compared against the underlying AEO section versions).</li>
                      <li><code>changed_section</code>: Optional narrowing for <code>changed_since</code>. Accepts an internal section key: <code>summary</code>, <code>best_for</code>, <code>buyer_profiles</code>, <code>use_cases</code>, <code>benefits</code>, <code>faq</code>, or <code>comparison</code>. Defaults to <code>overall</code> (any change).</li>
                    </ul>
                  </div>

                  {/* Category selector for example */}
                  <div className="flex items-center gap-3 py-2">
                    <Label className="text-sm whitespace-nowrap">Example category:</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-[300px]">
                        <SelectValue placeholder="Select a category to see example" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.name} value={cat.name}>
                            {cat.name} ({cat.product_count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="bg-zinc-900 text-zinc-100 p-3 rounded-md text-sm font-mono">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-zinc-400">Example — full export:</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-zinc-400 hover:text-zinc-100"
                        onClick={() => copyToClipboard(
                          `curl -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  "${baseUrl}/api/sites/${selectedSite?.id}/categories/${encodeURIComponent(selectedCategory || 'YOUR_CATEGORY')}/export/enriched-feed?format=json"`,
                          'curl-category'
                        )}
                      >
                        {copiedId === 'curl-category' ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <pre className="whitespace-pre-wrap">
{`curl -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  "${baseUrl}/api/sites/${selectedSite?.id}/categories/${encodeURIComponent(selectedCategory || 'YOUR_CATEGORY')}/export/enriched-feed?format=json"`}
                    </pre>
                  </div>

                  <div className="bg-zinc-900 text-zinc-100 p-3 rounded-md text-sm font-mono">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-zinc-400">Example — incremental sync (only changed products):</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-zinc-400 hover:text-zinc-100"
                        onClick={() => copyToClipboard(
                          `curl -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  "${baseUrl}/api/sites/${selectedSite?.id}/categories/${encodeURIComponent(selectedCategory || 'YOUR_CATEGORY')}/export/enriched-feed?format=json&filterByAeoStatus=true&includeProductAEO=true&changed_since=2026-04-01T00:00:00Z&include_section_versions=true"`,
                          'curl-category-versioning'
                        )}
                      >
                        {copiedId === 'curl-category-versioning' ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <pre className="whitespace-pre-wrap">
{`curl -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  "${baseUrl}/api/sites/${selectedSite?.id}/categories/${encodeURIComponent(selectedCategory || 'YOUR_CATEGORY')}/export/enriched-feed?format=json&filterByAeoStatus=true&includeProductAEO=true&changed_since=2026-04-01T00:00:00Z&include_section_versions=true"`}
                    </pre>
                  </div>
                </div>

                {/* Export by Question Wheel Endpoint */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">POST</Badge>
                    <span className="font-semibold">Export by Question Wheel</span>
                  </div>
                  <div className="bg-muted p-3 rounded-md font-mono text-sm break-all">
                    /api/sites/{'{siteId}'}/question-wheels/{'{wheelId}'}/export-feed
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium mb-1">Request Body (JSON):</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>format</code>: 'csv' | 'json' (default: csv)</li>
                      <li><code>includeStatus</code>: array of statuses ['approved', 'pending']</li>
                    </ul>
                  </div>

                  <div className="bg-zinc-900 text-zinc-100 p-3 rounded-md text-sm font-mono">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-zinc-400">Example:</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-zinc-400 hover:text-zinc-100"
                        onClick={() => copyToClipboard(
                          `curl -X POST -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"format": "csv", "includeStatus": ["approved", "pending"]}' \\
  "${baseUrl}/api/sites/${selectedSite?.id}/question-wheels/WHEEL_ID/export-feed"`,
                          'curl-wheel'
                        )}
                      >
                        {copiedId === 'curl-wheel' ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <pre className="whitespace-pre-wrap">
{`curl -X POST -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"format": "csv", "includeStatus": ["approved", "pending"]}' \\
  "${baseUrl}/api/sites/${selectedSite?.id}/question-wheels/WHEEL_ID/export-feed"`}
                    </pre>
                  </div>
                </div>

                {/* Response Fields */}
                <div className="space-y-3 pt-4 border-t">
                  <span className="font-semibold">Response Fields (per product)</span>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">Core Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>id, sku, title, brand</li>
                        <li>category, price, currency</li>
                        <li>description, url</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">Enrichment Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>qa_count - Total questions</li>
                        <li>qa_answered - Answered questions</li>
                        <li>top_questions - Top 5 questions</li>
                        <li>category_question_matches</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">AEO Category Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>aeo_featured_snippet</li>
                        <li>aeo_category_purpose</li>
                        <li>aeo_buyer_guide_intro</li>
                        <li>aeo_narrative</li>
                        <li>aeo_category_status</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">AEO FAQ Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>aeo_faq_questions</li>
                        <li>aeo_faq_answers</li>
                        <li>aeo_faq_count</li>
                        <li>aeo_approved_at</li>
                        <li>aeo_approved_by</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">Product AEO Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>aeo_product_why_buy</li>
                        <li>aeo_product_best_for</li>
                        <li>aeo_product_differentiators</li>
                        <li>aeo_product_faqs</li>
                        <li>aeo_product_status</li>
                        <li>aeo_product_approved_at</li>
                        <li>aeo_product_reviewer</li>
                        <li>aeo_product_last_published_at</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
